import { Component, ElementRef, ViewChild } from '@angular/core';
import { User, Show } from '@tytapp/models';
import { UserService } from '@tytapp/user';
import { ActivatedRoute } from '@angular/router';
import { FormInput, FormComponent, AppConfig, sleep, PageService, PageComponent } from '@tytapp/common';
import { Subscription } from 'rxjs';
import { UsersApi, ShowsApi, ApiTopic, TopicApi, FollowApi, ApiFollowedShow } from 'api';
import { SelectorTopic } from '@tytapp/common-ui';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { iframeResizer } from 'iframe-resizer';
import { PageApiService } from 'common-ui';
import { environment } from '@tytapp/environment';

export interface SettingsSection {
  id : string;
  label : string;
  hidden? : boolean;
  icon? : string;
  externalUrl? : string;
  openInNewTab? : boolean;
  defaultSubPage? : string;
  action? : Function;
  subpages? : SettingsSection[];
  doNotShowTopHeading? : boolean;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent extends PageComponent implements FormComponent {

  constructor(
    private userService : UserService,
    private route : ActivatedRoute,
    private appConfig : AppConfig,
    private userApi : UsersApi,
    private topicApi : TopicApi,
    private showApi : ShowsApi,
    private followApi : FollowApi,
    private domSanitizer : DomSanitizer,
    private pageApiService : PageApiService,
    pageService : PageService
  ) {
    super(pageService);
  }

  newEmail : string;
  newEmail2 : string;
  newUsername : string;
  usernameError : string = null;

  emailError : string = null;
  accountSuccess : string = null;

  currentPassword : string;
  newPassword : string;
  newPassword2 : string;
  passwordError : string = null;
  genericErrorMessage = 'An unknown error occurred. Please try again, if this issue persists contact support@tytnetwork.com';

  async changePassword() {
    this.passwordError = null;
    this.accountSuccess = null;

    if (!this.newPassword) {
      this.passwordError = 'Please enter the password you would like to use.';
      return;
    }

    if (this.newPassword != this.newPassword2) {
      this.passwordError = 'The password you provided does not match the confirmation.';
      return;
    }

    // if (this.newPassword.length < 7) {
    //   this.passwordError = 'Your password must be at least 7 characters long.';
    //   return;
    // }

    this.submitting = true;

    try {
      // await sleep(5*1000);
      // throw new Error('Uh oh!');

      await this.userApi.resetMyPassword({
        existing_password: this.currentPassword,
        new_password: this.newPassword
      }).toPromise();

      this.user.missing_password = false;
      this.userService.locallyUpdateUser(this.user);

    } catch (e) {
      if (e.json)
        e = e.json();

      this.submitting = false;
      this.passwordError = e.message || e.error || this.genericErrorMessage;
      return;
    }

    this.accountSuccess = 'Your password has been changed.';
    this.submitting = false;

    this.router.navigateByUrl('/settings/account');
  }

  async changeUsername() {
    if (!this.user)
      return;

    if (this.user.username) {
      alert(`Looks like you already have a username (${this.user.username}). You may contact support@tytnetwork.com if you wish to change it.`);
      return;
    }

    this.submitting = true;
    try {
      await this.userApi.updateUser({username: this.newUsername}).toPromise();
      this.submitting = false;

      this.user.username = this.newUsername;
      this.userService.locallyUpdateUser(this.user);

    } catch (e) {
      if (e.json)
        e = e.json();

      this.submitting = false;
      this.usernameError = e.message || e.error || this.genericErrorMessage;
      return;
    }

    let returnURL = this.route.snapshot.queryParamMap.get('return');
    if (returnURL)
      this.router.navigateByUrl(returnURL);
    else
      this.router.navigateByUrl(`/settings/account`);
  }

  async changeEmail() {

    this.emailError = null;
    this.accountSuccess = null;

    if (!this.newEmail) {
      this.emailError = 'Please enter the email address you would like to use.';
      return;
    }

    if (this.newEmail != this.newEmail2) {
      this.emailError = 'The email address you provided does not match the confirmation.';
      return;
    }

    let emailError = this.userService.validateEmail(this.newEmail);
    if (emailError) {
      this.emailError = emailError;
      return;
    }

    this.submitting = true;

    try {
      await this.userApi.updateUser({email: this.newEmail}).toPromise();
      this.user.email = this.newEmail;
      this.userService.locallyUpdateUser(this.user);

    } catch (e) {
      if (e.json)
        e = e.json();

      this.submitting = false;
      this.emailError = e.message || e.error || this.genericErrorMessage;
      return;
    }

    this.accountSuccess = 'Your email address has been changed.';
    this.submitting = false;

    this.router.navigateByUrl('/settings/account');
  }

  sections : SettingsSection[] = [];

  private _userChangedSub : Subscription = null;
  private disableRedirect : boolean = false;

  valuePropReason = "Stand with the only news network that stands **with you.**";
  reason = "Follow Your Favorites. Sign up for **free**";

  @FormInput()
  newsletter = false;

  get joinUrl() {
    return environment.urls.purchase;
  }

  membershipAccountUrl : SafeHtml;
  membershipSettingsUrl : SafeHtml;
  termsUrl : SafeHtml;
  privacyUrl : SafeHtml;
  oneTimeContributionUrl : SafeHtml;

  user : User;
  showTopics : SelectorTopic[];
  sectionReady : boolean = false;
  followedShows : string[];
  topics : ApiTopic[];
  categoryTopics : ApiTopic[];
  hostTopics : ApiTopic[];
  candidateTopics : ApiTopic[];
  politicianTopics : ApiTopic[];

  followedCategoryTopics : string[];
  followedHostTopics : string[];
  followedPoliticianTopics : string[];
  followedShowTopics : string[];
  followedCandidateTopics : string[];

  async loadTopics() {
    this.topics = await this.topicApi.all().toPromise();
    this.categoryTopics = this.getTopicsOfType('topic');
    this.hostTopics = this.getTopicsOfType('host');
    this.candidateTopics = this.getTopicsOfType('candidate');
    this.politicianTopics = this.getTopicsOfType('politician');

    let followedTopics = await this.followApi.getFollows().toPromise();

    this.followedCategoryTopics = followedTopics.filter(x => x.topic_type === 'topic').map(x => x.slug);
    this.followedHostTopics = followedTopics.filter(x => x.topic_type === 'host').map(x => x.slug);
    this.followedPoliticianTopics = followedTopics.filter(x => x.topic_type === 'politician').map(x => x.slug);
    this.followedCandidateTopics = followedTopics.filter(x => x.topic_type === 'candidate').map(x => x.slug);
  }

  isMainSection = false;

  getTopicsOfType(type : string) {
    if (!this.topics)
      return [];

    return this.topics.filter(x => x.topic_type === type);
  }

  afterProfileSave() {
    let returnURL = this.route.snapshot.queryParamMap.get('return');
    if (returnURL)
      this.router.navigateByUrl(returnURL);

  }

  async loadSection() {
    this.sectionReady = false;

    if (this.sectionId === 'memberships' || this.sectionId === 'affiliate') {

      let returnUrl = this.route.snapshot.queryParamMap.get('return');
      let url = `${environment.urls.accounts}/settings/${this.sectionId}`;

      if (returnUrl)
        url = `${url}?return=${encodeURIComponent(returnUrl)}`;

      if (typeof window !== undefined)
        window.location.replace(url);
    }

    if (this.sectionId === 'terms' || this.sectionId === 'privacy' || this.sectionId === 'memberships-settings') {
      setTimeout(() => {
        iframeResizer({ log: true }, '.accounts-subframe');
      }, 500);

    } else if (this.sectionId === 'content/shows') {

      if (!this.showTopics) {
        let shows : Show[];
        let followedShows : ApiFollowedShow[];

        let results = await Promise.all([
          this.showApi.all().toPromise(),
          this.followApi.getFollowedShows().toPromise()
        ]);

        shows = results[0] as any;

        let isFollowable = (x : Show) => (x.is_followable === null) ? true : x.is_followable;
        shows = shows.filter(x => isFollowable(x) && !x.is_past_show);

        followedShows = results[1] as any;

        this.showTopics = shows.map(x => <SelectorTopic>{
          id: `show:${x.id}`, slug: x.id, name: x.name, thumbnail_square: x.logo_square
        });
        this.followedShowTopics = followedShows.map(x => x.contentful_show_id);
      }
    } else if (this.sectionId === 'content/topics') {
      this.loadTopics();
    } else if (this.sectionId === 'content/hosts') {
      this.loadTopics();
    } else if (this.sectionId === 'content/candidates') {
      this.loadTopics();
    } else if (this.sectionId === 'content/politicians') {
      this.loadTopics();
    }

    this.sectionReady = true;
  }

  savingTopics = false;

  async saveCategoryTopics() {
    this.savingTopics = true;
    await this.saveTopicFollows(this.categoryTopics, this.followedCategoryTopics);
    await sleep(500);
    this.pageApiService.bust('home');
    this.userService.onboarding = false;
    this.savingTopics = false;
    this.router.navigateByUrl('/settings/content/shows');
  }

  async saveShowTopics() {
    this.savingTopics = true;
    //await this.saveShowTopics(this.categoryTopics, this.followedCategoryTopics);
    await this.followApi.patchFollowedShows({
      shows: <any> this.showTopics.map(x => ({
        contentful_show_id: x.slug,
        is_followed: this.followedShowTopics.includes(x.slug)
      }))
    }).toPromise()

    await sleep(500);
    this.pageApiService.bust('home');
    this.userService.onboarding = false;
    this.savingTopics = false;
    this.router.navigateByUrl('/settings/content/hosts');
  }

  async saveHostTopics() {
    this.savingTopics = true;
    await this.saveTopicFollows(this.hostTopics, this.followedHostTopics);
    await sleep(500);
    this.pageApiService.bust('home');
    this.userService.onboarding = false;
    this.savingTopics = false;
    //this.router.navigateByUrl('/settings/content/candidates');
    // this.router.navigateByUrl('/settings/content/politicians');
    this.router.navigateByUrl('/settings/content/done');
  }

  async savePoliticianTopics() {
    this.savingTopics = true;
    await this.saveTopicFollows(this.politicianTopics, this.followedPoliticianTopics);
    await sleep(500);
    this.savingTopics = false;

    this.pageApiService.bust('home');
    this.userService.onboarding = false;
    this.router.navigateByUrl('/settings/content/done');
  }

  async saveCandidateTopics() {
    this.savingTopics = true;
    await this.saveTopicFollows(this.candidateTopics, this.followedCandidateTopics);
    await sleep(500);
    this.pageApiService.bust('home');
    this.userService.onboarding = false;
    this.savingTopics = false;
    this.router.navigateByUrl('/settings/content/done');
  }

  async saveTopicFollows(topics : ApiTopic[], followed : string[]) {
    if (!this.topics)
      return;

    await this.followApi.patchFollows({
      topics: topics.map(topic => (<any>{
        slug: topic.slug,
        is_followed: followed.includes(topic.slug)
      }))
    }).toPromise();
  }

  get section(): SettingsSection {
    let mainSectionId = this.sectionId;
    let secondarySectionId = null;

    if (this.sectionId.includes('/')) {
      mainSectionId = this.sectionId.replace(/\/.*/, '');
      secondarySectionId = this.sectionId.substr(mainSectionId.length);
    }

    return this.sections.find(x => x.id === mainSectionId);
  }

  get mainSectionId(): string {

    let mainSectionId = this.sectionId;
    let secondarySectionId = null;

    if (this.sectionId.includes('/')) {
      mainSectionId = this.sectionId.replace(/\/.*/, '');
      secondarySectionId = this.sectionId.substr(mainSectionId.length);
    }

    return mainSectionId;
  }

  get secondarySectionId(): string {
    let secondarySectionId = null;

    if (this.sectionId.includes('/')) {
      let mainSectionId = this.sectionId.replace(/\/.*/, '');
      secondarySectionId = this.sectionId.substr(mainSectionId.length + 1);
    }

    return secondarySectionId;
  }

  get secondarySection(): SettingsSection {
    let mainSection = this.section;

    if (!mainSection)
      return null;

    return (mainSection.subpages || []).find(x => x.id === this.secondarySectionId);
  }

  sectionId = 'profile';

  addSection(afterId : string, section : SettingsSection) {
    if (afterId) {
      let afterIndex = this.sections.findIndex(x => x.id === afterId);
      this.sections.splice(afterIndex + 1, 0, section);
    } else {
      this.sections.push(section);
    }
  }

  @ViewChild('accountsIframe')
  accountsIframeElement : ElementRef<HTMLIFrameElement>;

  ngAfterViewInit() {
  }

  onboarding = false;
  enableYouTubeLogin = false;

  async init(reasonOrParams : string | Object = null) {
    this.subscribe(this.userService.onboardingChanged, onboarding => this.onboarding = onboarding);

    this.membershipAccountUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.urls.accounts}/account?chromeless=1`);
    this.membershipSettingsUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.urls.accounts}/membership-settings?chromeless=1`);
    this.termsUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.urls.accounts}/terms?chromeless=1`);
    this.privacyUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.urls.accounts}/privacy?chromeless=1`);
    this.oneTimeContributionUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.urls.accounts}/settings/one-time-contribution?chromeless=1`);

    let self = this;

    this.sections = [
      {
        id: 'profile',
        icon: '<img src="/assets/icons-2019/profile.svg" />',
        label: 'Profile',
        subpages: []
      }, {
        id: 'account',
        icon: '<img src="/assets/icons-2019/profile.svg" />',
        label: 'Account',
        subpages: [
          {
            id: 'change-email',
            label: 'Change Email',
            hidden: true,
            icon: '<img src="/assets/icons-2019/profile.svg" />'
          }, {
            id: 'change-password',
            label: 'Change Password',
            hidden: true,
            icon: '<img src="/assets/icons-2019/profile.svg" />'
          }, {
            id: 'change-username',
            label: 'Choose Username',
            hidden: true,
            icon: '<img src="/assets/icons-2019/profile.svg" />'
          }
        ]
      }, {
        id: 'memberships-settings',
        icon: '<img src="/assets/icons-2019/plus_membership.svg" />',
        label: 'Plus Memberships',
        externalUrl: `${this.accountsUrl}/settings/memberships`,
      }, {
        id: 'affiliate',
        icon: '<img src="/assets/tap/affiliate_icon_white.svg" />',
        label: 'Affiliate Program',
        hidden: true,
        externalUrl: `${this.accountsUrl}/settings/affiliate`,
        openInNewTab: false
      }, {
        id: 'newsletters',
        icon: '<img src="/assets/icons-2019/newsletters.svg" />',
        label: 'Newsletters'
      }, {
        id: 'content',
        icon: '<img src="/assets/icons-2019/content.svg" />',
        label: 'Content',
        defaultSubPage: 'topics',
        subpages: [
          {
            id: 'topics',
            label: 'Topics',
            icon: '<img src="/assets/icons-2019/topics.svg" />'
          },
          {
            id: 'shows',
            label: 'Shows',
            icon: '<img src="/assets/icons-2019/shows.svg" />'
          },
          {
            id: 'hosts',
            label: 'Hosts',
            icon: '<img src="/assets/icons-2019/hosts.svg" />'
          },
          {
            id: 'politicians',
            label: 'Politicians',
            icon: '<img src="/assets/icons-2019/candidates.svg" />',
            hidden: true
          },
          {
            id: 'done',
            hidden: true,
            label: 'Saved',
            icon: ''
          },
          {
            id: 'candidates',
            label: 'Candidates',
            icon: '<i class="fas fa-hand-holding-heart"></i>',
            hidden: true
          }
        ]
      }, {
        id: 'faq',
        icon: '<img src="/assets/icons-2019/contact_support.svg" />',
        label: 'Get Help',
        externalUrl: 'https://help.tyt.com/',
        openInNewTab: true
      }, {
        id: 'terms',
        icon: '<img src="/assets/icons-2019/terms_of_service.svg" />',
        label: 'Terms of Service',
        // externalUrl: `${this.accountsUrl}/terms`,
        // openInNewTab: true
      }, {
        id: 'privacy',
        icon: '<img src="/assets/icons-2019/privacy_policy.svg" />',
        label: 'Privacy Policy',
        // externalUrl: `${this.accountsUrl}/privacy`,
        // openInNewTab: true
      }, {
        id: 'core-values',
        icon: '<img src="/assets/icons-2019/privacy_policy.svg" />',
        label: 'TYT Core Values',
        externalUrl: `${this.accountsUrl}/settings/core-values`,
        doNotShowTopHeading: true
      }, {
        id: 'logout',
        icon: '<img src="/assets/icons-2019/log_out.svg" />',
        label: 'Logout',
        action() {
          self.userService.completeLogout();
          self.userService.user = null;
          self.router.navigateByUrl('/home');
        }
      }
    ];

    this.route.paramMap.subscribe(params => {

      this.isMainSection = !params.has('section');

      if (params.get('subsection') && params.get('section')) {
        this.sectionId = `${params.get('section')}/${params.get('subsection')}`;
      } else {
        this.sectionId = params.get('section') || 'profile';
      }

      this.loadSection();
    });

    this.subscribe(this.userService.userChanged, async user => {
      let affiliateSection = this.sections.find(x => x.id === 'affiliate');
      if (await this.appConfig.featureEnabled('apps.accounts.enable_affiliate_signup')) {
        affiliateSection.hidden = false;
      } else {
        affiliateSection.hidden = !user || !user.affiliate;
      }

      if (!user || this.disableRedirect)
        return;

      this.user = user;
    });

    if (await this.shell.hasFeature('apps.web.enable_privacy_controls')) {      
      this.addSection('core-values', {
        id: 'privacy-controls',
        icon: '<img src="/assets/privacy/privacy_controls.svg" />',
        label: 'Privacy Controls',
        externalUrl: `${this.accountsUrl}/settings/privacy-controls`,
      });
    }

    if (await this.shell.hasFeature('apps.web.enable_in_settings_otc')) {      
      this.addSection('memberships-settings', {
        id: 'one-time-contribution',
        icon: '<img src="/assets/one-time-contribution.svg" />',
        externalUrl: `${this.accountsUrl}/settings/one-time-contribution`,
        label: 'One Time Donations'
      });
    }
  
  }

  submitting = false;

  get accountsUrl() {
    return environment.urls.accounts;
  }

  async submit() {
    // CRITICAL SECTION: NO ASYNC //
    if (this.submitting)
      return;
    this.submitting = true;

    // CRITICAL SECTION: NO ASYNC //

    // Make sure the UI has updated before we proceed
    await sleep(0);

    // TODO
  }
}
